import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import styles from './AdminErrorDashboard.module.css';
import { errorApi, type ErrorLog as ApiErrorLog, type ErrorLogDetail as ApiErrorLogDetail, type ErrorStats as ApiErrorStats } from '../../services/errorApi';
import { userApi } from '../../services/userApi';

interface Role {
  Roles_ID: string;
  name: string;
}

interface User {
  User_ID: string;
  firstname: string;
  lastname: string;
  mail: string;
  picture: string;
  roles?: Array<Role>;
}

interface ErrorLog extends ApiErrorLog {
  user?: User; // Erweitert um User-Informationen
}

interface ErrorLogDetail extends ApiErrorLogDetail {
  user?: User;
}

interface ErrorStats extends ApiErrorStats {
  recent_errors: ErrorLog[];
}

const Admin_Error_Dashboard: React.FC = () => {
  const [errors, setErrors] = useState<ErrorLog[]>([]);
  const [filteredErrors, setFilteredErrors] = useState<ErrorLog[]>([]);
  const [selectedError, setSelectedError] = useState<ErrorLogDetail | null>(null);
  const [stats, setStats] = useState<ErrorStats | null>(null);
  const [users, setUsers] = useState<Map<string, User>>(new Map());
  const [searchTerm, setSearchTerm] = useState('');
  const [methodFilter, setMethodFilter] = useState<string>('');
  const [pathFilter, setPathFilter] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [showModal, setShowModal] = useState<boolean>(false);
  const navigate = useNavigate();
  const isMountedRef = useRef<boolean>(true);

  // Lade User-Daten für gegebene User IDs
  const loadUserData = async (userIds: string[]) => {
    try {
      const userMap = new Map<string, User>();
      
      // Lade User-Daten für jede ID
      const userPromises = userIds.map(async (userId) => {
        try {
          const userData = await userApi.getUserById(userId);
          userMap.set(userId, {
            ...userData,
            User_ID: userData.User_ID ?? userId
          });
        } catch (error) {
          console.warn(`Failed to load user data for ID ${userId}:`, error);
          // Erstelle einen Fallback-User-Eintrag
          userMap.set(userId, {
            User_ID: userId,
            firstname: 'Unknown',
            lastname: 'User',
            mail: '',
            picture: '',
            roles: []
          });
        }
      });
      
      await Promise.all(userPromises);
      setUsers(userMap);
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  // Fetch errors and stats on mount
  useEffect(() => {
    let isComponentMounted = true;

    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch errors and stats in parallel
        const [errorsData, statsData] = await Promise.all([
          errorApi.getAllErrors(),
          errorApi.getErrorStats()
        ]);
        
        if (isComponentMounted) {
          setErrors(errorsData);
          setFilteredErrors(errorsData);
          setStats(statsData);
          
          // Lade User-Informationen für alle unique User IDs
          const userIds = Array.from(new Set(
            errorsData
              .map((error: ErrorLog) => error.user_id?.trim())
              .filter((id): id is string => Boolean(id))
          ));
          if (userIds.length > 0) {
            await loadUserData(userIds);
          }
          
          setLoading(false);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load error logs. Please try again later.', {
          position: "top-right",
          autoClose: 3000
        });
        if (isComponentMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isComponentMounted = false;
      isMountedRef.current = false;
    };
  }, []);

  // Filter errors when search term or filters change
  useEffect(() => {
    let filtered = errors;

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(error =>
        error.error_code.toLowerCase().includes(searchLower) ||
        error.error_message.toLowerCase().includes(searchLower) ||
        error.path.toLowerCase().includes(searchLower)
      );
    }

    if (methodFilter) {
      filtered = filtered.filter(error => error.method === methodFilter);
    }

    if (pathFilter) {
      filtered = filtered.filter(error => error.path.includes(pathFilter));
    }

    setFilteredErrors(filtered);
  }, [searchTerm, methodFilter, pathFilter, errors]);

  const handleErrorClick = async (errorCode: string) => {
    try {
      const errorDetail = await errorApi.getErrorDetail(errorCode);
      setSelectedError(errorDetail);
      setShowModal(true);
    } catch (error) {
      console.error('Error fetching error detail:', error);
      toast.error('Failed to load error details', {
        position: "top-right",
        autoClose: 3000
      });
    }
  };

  const handleDeleteError = async (errorCode: string) => {
    toast.warn(
      <div style={{ minWidth: '300px' }}>
        <p className="mb-3 fw-bold">Are you sure you want to delete this error log?</p>
        <div className="d-flex justify-content-center gap-3">
          <button 
            className="btn btn-secondary px-4" 
            onClick={() => toast.dismiss()}
          >
            Cancel
          </button>
          <button 
            className="btn btn-danger px-4" 
            onClick={async () => {
              toast.dismiss();
              try {
                await errorApi.deleteError(errorCode);
                setErrors(prevErrors => prevErrors.filter(error => error.error_code !== errorCode));
                if (selectedError && selectedError.error_code === errorCode) {
                  setShowModal(false);
                  setSelectedError(null);
                }
                toast.success('Error log deleted successfully', {
                  position: "top-right",
                  autoClose: 3000
                });
                
                // Refresh stats
                const updatedStats = await errorApi.getErrorStats();
                setStats(updatedStats);
              } catch (error) {
                console.error('Error deleting error log:', error);
                toast.error('Failed to delete error log', {
                  position: "top-right",
                  autoClose: 3000
                });
              }
            }}
          >
            Delete
          </button>
        </div>
      </div>,
      {
        position: "top-center",
        autoClose: false,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        closeButton: false,
        className: styles.deleteConfirmationToast,
        style: { 
          maxWidth: '400px', 
          width: '100%',
          padding: '10px'
        }
      }
    );
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1)
      .toString().padStart(2, '0')}.${date.getFullYear()} ${date.getHours().toString()
      .padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  };

  const formatUserInfo = (userId?: string): React.ReactNode => {
    if (!userId) return null;
    
    const user = users.get(userId);
    if (!user) {
      return <span className="text-muted">User ID: {userId}</span>;
    }
    
    return (
      <div className={styles.userInfo}>
        <span className={styles.userName}>
          {user.firstname} {user.lastname}
        </span>
        {user.roles && user.roles.length > 0 && (
          <div className={styles.userRoles}>
            {user.roles.map((role, index) => (
              <span key={role.Roles_ID || index} className="badge bg-secondary me-1">
                {role.name}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  };

  const getMethodBadgeClass = (method: string) => {
    switch (method.toUpperCase()) {
      case 'GET':
        return 'badge bg-success';
      case 'POST':
        return 'badge bg-primary';
      case 'PUT':
        return 'badge bg-warning text-dark';
      case 'DELETE':
        return 'badge bg-danger';
      case 'CLIENT_ERROR':
        return 'badge bg-danger';
      default:
        return 'badge bg-secondary';
    }
  };

  return (
    <div className="container mt-4">
            
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/admin"><i className="bi bi-house-door me-1"></i>Admin</Link>
          </li>
          <li className="breadcrumb-item active">Error Log Management</li>
        </ol>
      </nav>

      {/* Statistics Cards */}
      {stats && (
        <div className="row mb-4">
          <div className="col-md-4 mb-3">
            <div className={`${styles.statCard} ${styles.statCardDanger}`}>
              <div className={styles.statCardIcon}>
                <i className="bi bi-exclamation-triangle-fill"></i>
              </div>
              <div className={styles.statCardContent}>
                <h3>{stats.total_errors}</h3>
                <p>Total Errors</p>
              </div>
            </div>
          </div>
          <div className="col-md-4 mb-3">
            <div className={`${styles.statCard} ${styles.statCardInfo}`}>
              <div className={styles.statCardIcon}>
                <i className="bi bi-calendar-day-fill"></i>
              </div>
              <div className={styles.statCardContent}>
                <h3>{stats.errors_today}</h3>
                <p>Today</p>
              </div>
            </div>
          </div>
          <div className="col-md-4 mb-3">
            <div className={`${styles.statCard} ${styles.statCardSuccess}`}>
              <div className={styles.statCardIcon}>
                <i className="bi bi-calendar-week-fill"></i>
              </div>
              <div className={styles.statCardContent}>
                <h3>{stats.errors_this_week}</h3>
                <p>This Week</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="row mt-4">
        {/* Error Timeline Tree - Left Side */}
        <div className="col-md-3 border-end">
          <h5>🗂️ Error Timeline</h5>
          <div className={styles.timelineContainer}>
            {stats?.most_common_paths.map((pathData, index) => (
              <div key={index} className={styles.timelineItem}>
                <div className={styles.timelinePath}>{pathData.path}</div>
                <div className={styles.timelineCount}>
                  <span className="badge bg-primary">{pathData.count}</span>
                </div>
              </div>
            ))}
          </div>
          
          <h6 className="mt-4">HTTP Methods</h6>
          <div className={styles.levelDistribution}>
            {stats && Object.entries(stats.error_methods_distribution).map(([method, count]) => (
              <div key={method} className={styles.levelItem}>
                <span className={getMethodBadgeClass(method)}>{method}</span>
                <span className="ms-2">{count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Error List - Right Side */}
        <div className="col-md-9">
          {/* Filters */}
          <div className="row mb-3">
            <div className="col-md-4">
              <input 
                type="text" 
                className={`form-control ${styles.searchInput}`} 
                placeholder="Search by Error Code, Path or Message..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <select 
                className="form-select" 
                value={methodFilter} 
                onChange={(e) => setMethodFilter(e.target.value)}
              >
                <option value="">All Methods</option>
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="DELETE">DELETE</option>
                <option value="CLIENT_ERROR">Client Error</option>
              </select>
            </div>
            <div className="col-md-3">
              <input 
                type="text" 
                className="form-control" 
                placeholder="Filter by path..."
                value={pathFilter}
                onChange={(e) => setPathFilter(e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <button 
                className="btn btn-outline-secondary w-100"
                onClick={() => {
                  setSearchTerm('');
                  setMethodFilter('');
                  setPathFilter('');
                }}
              >
                Clear
              </button>
            </div>
          </div>

          {/* Error List */}
          <div className={styles.errorListContainer}>
            {loading ? (
              <div className="text-center p-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <div className="mt-2">Loading error logs...</div>
              </div>
            ) : filteredErrors.length === 0 ? (
              <div className="text-center p-5">
                <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '3rem' }}></i>
                <h5 className="mt-3">No errors found</h5>
                <p className="text-muted">
                  {errors.length > 0 ? 'No errors match your search criteria' : 'No error logs available'}
                </p>
              </div>
            ) : (
              <div className={styles.errorList}>
                {filteredErrors.map(error => (
                  <div key={error.error_code} className={styles.errorItem}>
                    <div className={styles.errorHeader}>
                      <div className={styles.errorInfo}>
                        <span className={getMethodBadgeClass(error.method)}>{error.method}</span>
                        <span className={styles.errorTime}>{formatDate(error.timestamp)}</span>
                        <span className={styles.errorPath}>{error.path}</span>
                      </div>
                      <div className={styles.errorActions}>
                        <button 
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => handleErrorClick(error.error_code)}
                        >
                          <i className="bi bi-eye-fill"></i>
                        </button>
                        <button 
                          className="btn btn-sm btn-outline-danger ms-1"
                          onClick={() => handleDeleteError(error.error_code)}
                        >
                          <i className="bi bi-trash-fill"></i>
                        </button>
                      </div>
                    </div>
                    <div className={styles.errorMessage}>
                      {error.error_message}
                    </div>
                    <div className={styles.errorMeta}>
                      <span>Code: {error.error_code}</span>
                      {error.context_info && <span className="ms-3">Context available</span>}
                    </div>
                    {error.user_id && (
                      <div className={styles.errorUser}>
                        <i className="bi bi-person-fill me-1"></i>
                        {formatUserInfo(error.user_id)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Error Detail Modal */}
      {showModal && selectedError && (
        <div className={`modal fade show ${styles.modal}`} style={{ display: 'block' }} onClick={() => setShowModal(false)}>
          <div className="modal-dialog modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Error Details</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>Error Code:</strong> {selectedError.error_code}
                  </div>
                  <div className="col-md-6">
                    <strong>Method:</strong> <span className={getMethodBadgeClass(selectedError.method)}>{selectedError.method}</span>
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>Timestamp:</strong> {formatDate(selectedError.timestamp)}
                  </div>
                  <div className="col-md-6">
                    <strong>Path:</strong> {selectedError.path}
                  </div>
                </div>
                <div className="mb-3">
                  <strong>Message:</strong>
                  <div className={styles.codeBlock}>{selectedError.error_message}</div>
                </div>
                {selectedError.stacktrace && (
                  <div className="mb-3">
                    <strong>Stack Trace:</strong>
                    <pre className={styles.codeBlock}>{selectedError.stacktrace}</pre>
                  </div>
                )}
                {selectedError.context_info && (
                  <div className="mb-3">
                    <strong>Context Info:</strong>
                    <pre className={styles.codeBlock}>{selectedError.context_info}</pre>
                  </div>
                )}
                {selectedError.user_id && (
                  <div className="mb-3">
                    <strong>User Information:</strong>
                    <div className="mt-2">
                      {formatUserInfo(selectedError.user_id)}
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button 
                  className="btn btn-danger"
                  onClick={() => {
                    setShowModal(false);
                    handleDeleteError(selectedError.error_code);
                  }}
                >
                  Delete Error
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin_Error_Dashboard;
